import { API_BASE_URL as BASE } from '@/lib/apiBase'
import { authFetch, authHeaders, handleUnauthorized } from '@/lib/authFetch'
import type { OfflineSignatureMode } from '@/api/syncApi'

export type Tenant = {
  id: string
  networkId: string
  name: string
  status: 'active' | 'inactive'
  /** IANA timezone (e.g. "America/New_York") — resolves TODAY()/NOW() in this
   *  tenant's calculated-field formulas. Defaults to 'UTC'. */
  timezone: string
  /** Whole days after a maintenance task's due date before it is flagged
   *  overdue. 0 (the default) flags it the day after it is due. */
  maintenanceTaskOverdueGraceDays: number
  /** Hours a phone on the mobile surface may work offline; an action syncs
   *  only if it was done within this many hours of reaching the server. 1–24,
   *  default 12 (z8nrz7fh5c). */
  offlineWindowHours: number
  /** Whether phones on the mobile surface may sign actions with no
   *  connection. Default 'OFF' (z8nrz7fh5c). */
  offlineSignatureMode: OfflineSignatureMode
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await authFetch(`${BASE}${path}`, {
    ...init,
    headers: authHeaders(init?.headers),
  })
  if (!res.ok) {
    handleUnauthorized(res.status)
    const text = await res.text().catch(() => res.statusText)
    throw new Error(`API ${init?.method ?? 'GET'} ${path} failed (${res.status}): ${text}`)
  }
  return res.json() as Promise<T>
}

export type TenantMember = {
  id: string
  createdBy: string
  createdAt: string
  // The role this member holds IN THIS TENANT. Null means member with no role,
  // i.e. they can reach the tenant but have no permissions in it.
  roleId: string | null
  role: { slug: string; displayName: string; fullAccess: boolean } | null
  user: {
    id: string
    email: string
    username: string | null
    fullName: string | null
    enabled: boolean
  }
}

export type NotificationRecipientCandidate = {
  id: string
  email: string
  fullName: string | null
}

/** A role a notification rule can be addressed to, with its live headcount. */
export type NotificationRoleCandidate = {
  id: string
  name: string
  /** Enabled members holding this role in the tenant right now. */
  memberCount: number
}

export type NotificationRecipientsResponse = {
  members: NotificationRecipientCandidate[]
  superadmins: NotificationRecipientCandidate[]
  /** Addressable roles — sent alongside the people so the picker needs one request. */
  roles: NotificationRoleCandidate[]
}

export const tenantsApi = {
  list: () => request<Tenant[]>('/tenants'),

  create: (payload: { name: string }) =>
    request<Tenant>('/tenants', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  update: (
    id: string,
    payload: {
      name?: string
      status?: 'active' | 'inactive'
      timezone?: string
      maintenanceTaskOverdueGraceDays?: number
      offlineWindowHours?: number
      offlineSignatureMode?: OfflineSignatureMode
    },
  ) =>
    request<Tenant>(`/tenants/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  // 409 with per-table blocker counts while any data references the tenant.
  remove: (id: string) =>
    request<{ deleted: true }>(`/tenants/${id}`, { method: 'DELETE' }),

  listMembers: (id: string) => request<TenantMember[]>(`/tenants/${id}/members`),

  // Candidate recipients (this tenant's members + superadmins) for a
  // form-builder notification rule. Gated on `forms.edit`, not `users.manage`
  // — a form-authoring concern, delegable to whoever can edit forms.
  getNotificationRecipients: (tenantId: string) =>
    request<NotificationRecipientsResponse>(`/tenants/${tenantId}/notification-recipients`),

  addMembers: (id: string, members: { userId: string; roleId?: string }[]) =>
    request<TenantMember[]>(`/tenants/${id}/members`, {
      method: 'POST',
      body: JSON.stringify({ members }),
    }),

  // For tenant admins: resolving a user id needs GET /users, which is global and
  // superadmin-only, so they name the person by email instead. 404s when no
  // account has that address.
  addMemberByEmail: (tenantId: string, input: { email: string; roleId?: string }) =>
    request<TenantMember[]>(`/tenants/${tenantId}/members/by-email`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  // Onboarding someone with no account yet. Server-side this can never grant
  // platform access nor add them to another tenant, whatever is sent.
  createMember: (
    tenantId: string,
    input: {
      email: string
      firstName: string
      lastName: string
      temporaryPassword: string
      roleId?: string
    },
  ) =>
    request<TenantMember[]>(`/tenants/${tenantId}/members/create`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  setMemberRole: (id: string, userId: string, roleId: string | null) =>
    request<TenantMember[]>(`/tenants/${id}/members/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify({ roleId }),
    }),

  removeMember: (id: string, userId: string) =>
    request<{ removed: number }>(`/tenants/${id}/members/${userId}`, { method: 'DELETE' }),
}
